import React, { useState } from 'react';
import Button from '@ingka/button';
import JumboButton from '@ingka/jumbo-button';
import InlineMessage from '@ingka/inline-message';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import services from '../services';
import { mediaQueries } from '../styles/mediaQueries';
import { sendEventToParent } from '../eventPublisher';
import type { AppView } from '../types/app';

const StyledButton = styled(Button)<{ $fullWidth?: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const StyledJumboButton = styled(JumboButton)`
  width: 100%;
  ${mediaQueries.desktopUp} {
    width: 50%;
  }
`;
interface PayOrderButtonProps {
  payOrderLink: string;
  orderNo: string;
  buttonText: string | React.ReactNode;
  email?: string;
  view: AppView;
}

const PayOrderButton: React.FC<PayOrderButtonProps> = ({
  payOrderLink,
  orderNo,
  buttonText,
  email,
  view,
}) => {
  const { t } = useTranslation();
  const [hasPayOrderLinkError, setHasPayOrderLinkError] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const clickOnPayOrder = async (): Promise<void> => {
    setIsLoading(true);
    const link =
      payOrderLink ||
      (email && (await services.appointment.getPayOrderLink(orderNo, email)));

    if (link && typeof window !== 'undefined') {
      window.open(link, '_blank', 'noopener,noreferrer');
      if (view === 'alt') sendEventToParent('BOKA/BOKA_CLOSED', view);
    } else {
      setHasPayOrderLinkError(true);
    }
    setIsLoading(false);
  };

  if (hasPayOrderLinkError) {
    return (
      <InlineMessage
        variant="negative"
        title={t('components.error.payOrder.title')}
        body={t('components.error.payOrder.description')}
        data-cy="pay-order-link-error"
      />
    );
  }

  return (
    <>
      {view === 'alt' ? (
        <StyledButton
          $fullWidth
          type="emphasised"
          loading={isLoading}
          onClick={clickOnPayOrder}
          text={buttonText}
          data-cy="pay-now-button"
        />
      ) : (
        <StyledJumboButton
          loading={isLoading}
          onClick={clickOnPayOrder}
          label={t('pages.confirm.payOrderButtonText')}
          data-cy="pay-now-button"
        />
      )}
    </>
  );
};

export default PayOrderButton;
